import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const LeadForm = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lead, setLead] = useState(null);
  const [activeTab, setActiveTab] = useState('history');
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    job_title: '',
    phone: '',
    source: '',
    source_details: '',
    stage: 'new',
    qualification_status: 'new',
    lead_score: 0,
    lead_grade: '',
    next_follow_up: '',
    notes: ''
  });

  const isEditing = Boolean(leadId);

  const fetchActivities = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoadingActivities(true);
      const response = await api.get(`/leads/${leadId}/activities`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  }, [leadId]);

  const fetchLead = useCallback(async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const response = await api.get(`/leads/${leadId}`);
      const leadData = response.data;
      setLead(leadData);
      setFormData({
        email: leadData.email,
        first_name: leadData.first_name || '',
        last_name: leadData.last_name || '',
        company: leadData.company || '',
        job_title: leadData.job_title || '',
        phone: leadData.phone || '',
        source: leadData.source || '',
        source_details: leadData.source_details || '',
        stage: leadData.stage || 'new',
        qualification_status: leadData.qualification_status || 'new',
        lead_score: leadData.lead_score || 0,
        lead_grade: leadData.lead_grade || '',
        next_follow_up: leadData.next_follow_up ? leadData.next_follow_up.split('T')[0] : '',
        notes: leadData.source_details || ''
      });
    } catch (error) {
      console.error('Error fetching lead:', error);
      setError('Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (leadId) {
      fetchLead();
      fetchActivities();
    }
  }, [leadId, fetchLead, fetchActivities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Clean up formData - remove empty strings and convert to null where needed
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        // Keep the value as-is, but don't include empty strings for optional fields
        if (value === '') {
          // For optional string fields, send null instead of empty string
          acc[key] = null;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});

      if (isEditing) {
        await api.put(`/leads/${leadId}`, cleanedData);
      } else {
        await api.post('/leads', cleanedData);
      }
      navigate('/leads');
    } catch (error) {
      console.error('Error saving lead:', error);
      setError(error.response?.data?.error || error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} lead`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this lead? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/leads/${leadId}`);
      navigate('/leads');
    } catch (error) {
      console.error('Error deleting lead:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to delete lead');
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/leads')}
          className="breadcrumb-link"
        >
          Leads
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditing ? 'Edit Lead' : 'New Lead'}
        </span>
      </nav>

      {/* Lead Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? '✏️ Edit Lead' : '➕ New Lead'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update lead information and tracking' : 'Add a new lead to your pipeline'}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          marginTop: '5px',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={() => navigate('/leads')}
              disabled={loading}
              className="btn btn-outline"
            >
              ✕ Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              form="lead-form"
            >
              {loading ? 'Saving...' : (isEditing ? '💾 Update' : '➕ Create Lead')}
            </button>
          </div>
        </div>

        <form id="lead-form" onSubmit={handleSubmit}>
          
          {/* Contact Information Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📧 Contact Information
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Email Address *
                </label>
                <input 
                  type="email" 
                  name="email"
                  required 
                  value={formData.email} 
                  onChange={handleInputChange}
                  placeholder="lead@company.com"
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Phone Number
                </label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone} 
                  onChange={handleInputChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  First Name
                </label>
                <input 
                  type="text" 
                  name="first_name"
                  value={formData.first_name} 
                  onChange={handleInputChange}
                  placeholder="John"
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Last Name
                </label>
                <input 
                  type="text" 
                  name="last_name"
                  value={formData.last_name} 
                  onChange={handleInputChange}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Company
                </label>
                <input 
                  type="text" 
                  name="company"
                  value={formData.company} 
                  onChange={handleInputChange}
                  placeholder="Acme Corp"
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Job Title
                </label>
                <input 
                  type="text" 
                  name="job_title"
                  value={formData.job_title} 
                  onChange={handleInputChange}
                  placeholder="Marketing Director"
                />
              </div>
            </div>
          </div>

          {/* Lead Qualification Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              🎯 Lead Qualification
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Stage
                </label>
                <select 
                  name="stage"
                  value={formData.stage} 
                  onChange={handleInputChange}
                >
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="lost">Lost</option>
                  <option value="won">Won</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Qualification Status
                </label>
                <select 
                  name="qualification_status"
                  value={formData.qualification_status} 
                  onChange={handleInputChange}
                >
                  <option value="new">New</option>
                  <option value="unqualified">Unqualified</option>
                  <option value="mql">MQL (Marketing Qualified)</option>
                  <option value="sql">SQL (Sales Qualified)</option>
                  <option value="qualified">Qualified</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Lead Grade
                </label>
                <select 
                  name="lead_grade"
                  value={formData.lead_grade} 
                  onChange={handleInputChange}
                >
                  <option value="">Not Graded</option>
                  <option value="A">A - Hot Lead</option>
                  <option value="B">B - Warm Lead</option>
                  <option value="C">C - Cold Lead</option>
                  <option value="D">D - Long-term</option>
                  <option value="F">F - Unqualified</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Lead Score
                </label>
                <input 
                  type="number" 
                  name="lead_score"
                  value={formData.lead_score} 
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  placeholder="0-100"
                />
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Next Follow-up Date
                </label>
                <input 
                  type="date" 
                  name="next_follow_up"
                  value={formData.next_follow_up} 
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Source Tracking Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📊 Source & Tracking
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Lead Source
                </label>
                <select 
                  name="source"
                  value={formData.source} 
                  onChange={handleInputChange}
                >
                  <option value="">Select Source</option>
                  <option value="website">Website</option>
                  <option value="landing_page">Landing Page</option>
                  <option value="social_media">Social Media</option>
                  <option value="email_campaign">Email Campaign</option>
                  <option value="paid_ads">Paid Ads</option>
                  <option value="referral">Referral</option>
                  <option value="event">Event/Webinar</option>
                  <option value="cold_outreach">Cold Outreach</option>
                  <option value="partner">Partner</option>
                  <option value="organic_search">Organic Search</option>
                  <option value="direct">Direct</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '600', 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Source Details
                </label>
                <input 
                  type="text" 
                  name="source_details"
                  value={formData.source_details} 
                  onChange={handleInputChange}
                  placeholder="e.g., Facebook Ad Campaign Q4"
                />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: 'var(--gray-800)', 
              marginBottom: '1rem',
              paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--gray-200)'
            }}>
              📝 Notes
            </h3>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Additional Notes
              </label>
              <textarea 
                name="notes"
                value={formData.notes} 
                onChange={handleInputChange}
                rows="6"
                placeholder="Add any additional information about this lead..."
              />
            </div>
          </div>
        </form>
      </div>

      {/* Tabs - Only show when editing */}
      {isEditing && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ 
            display: 'flex', 
            gap: '2rem', 
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '1.5rem'
          }}>
            <button
              type="button"
              onClick={() => setActiveTab('history')}
              style={{
                background: 'none',
                border: 'none',
                padding: '1rem 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                color: activeTab === 'history' ? 'var(--primary-600)' : 'var(--gray-600)',
                borderBottom: activeTab === 'history' ? '2px solid var(--primary-600)' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'all 0.2s'
              }}
            >
              📜 History
            </button>
          </div>

          {/* History Tab Content */}
          {activeTab === 'history' && (
          <div style={{ minHeight: '400px' }}>
            {loadingActivities ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-600)' }}>
                Loading activities...
              </div>
            ) : activities.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem',
                backgroundColor: 'var(--gray-50)',
                borderRadius: '8px',
                border: '1px dashed var(--gray-300)'
              }}>
                <p style={{ fontSize: '3rem', margin: '0 0 1rem 0' }}>📭</p>
                <p style={{ color: 'var(--gray-600)', fontSize: '1rem', margin: 0 }}>
                  No activity recorded yet
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {activities.map((activity, index) => {
                  const activityIcons = {
                    email_opened: '📧',
                    email_clicked: '🖱️',
                    page_view: '👁️',
                    form_submit: '📝',
                    phone_call: '📞',
                    meeting: '🤝',
                    note_added: '📋',
                    status_change: '🔄',
                    score_change: '📊'
                  };
                  
                  const icon = activityIcons[activity.activity_type] || '•';
                  const date = new Date(activity.occurred_at);
                  
                  return (
                    <div 
                      key={index}
                      style={{
                        padding: '1.25rem',
                        backgroundColor: 'white',
                        border: '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        display: 'flex',
                        gap: '1rem',
                        transition: 'box-shadow 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ 
                        fontSize: '1.5rem',
                        flexShrink: 0
                      }}>
                        {icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontWeight: '600', 
                          color: 'var(--gray-900)',
                          marginBottom: '0.25rem',
                          textTransform: 'capitalize'
                        }}>
                          {activity.activity_type.replace(/_/g, ' ')}
                        </div>
                        {activity.description && (
                          <div style={{ 
                            color: 'var(--gray-700)',
                            fontSize: '0.875rem',
                            marginBottom: '0.5rem'
                          }}>
                            {activity.description}
                          </div>
                        )}
                        <div style={{ 
                          color: 'var(--gray-500)',
                          fontSize: '0.75rem'
                        }}>
                          {date.toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadForm;
